import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import { Color } from '@/constants/color';
import { Button as NewButton, buttonVariants, type ButtonVariants } from '@/ui/Button/Button';

import type { ButtonSize, Props } from './types';

/**
 * The old API accepted any of 19 raw Tailwind colors; the new component only has 5
 * semantic colors. Map by closest visual intent — this is a lossy translation.
 */
export const colorToSemantic: Record<Color, NonNullable<ButtonVariants['color']>> = {
  [Color.AMBER]: 'warning',
  [Color.BLACK]: 'neutral',
  [Color.BLUE]: 'secondary',
  [Color.CYAN]: 'secondary',
  [Color.EMERALD]: 'primary',
  [Color.GRAY]: 'neutral',
  [Color.GREEN]: 'primary',
  [Color.INDIGO]: 'secondary',
  [Color.LIME]: 'primary',
  [Color.NEUTRAL]: 'neutral',
  [Color.ORANGE]: 'warning',
  [Color.RED]: 'destructive',
  [Color.SKY]: 'secondary',
  [Color.SLATE]: 'neutral',
  [Color.STONE]: 'neutral',
  [Color.TEAL]: 'primary',
  [Color.WHITE]: 'neutral',
  [Color.YELLOW]: 'warning',
  [Color.ZINC]: 'neutral'
};

export const sizeToSemantic: Record<ButtonSize, NonNullable<ButtonVariants['size']>> = {
  badge: 'xs',
  xs: 'xs',
  sm: 'sm',
  base: 'md',
  lg: 'lg',
  xl: 'xl'
};

// Old Button always rendered a solid bg + border, which for Color.WHITE (bg-white) looked
// like a plain/subtle outline button rather than a filled one. Match that look here, since
// the new "neutral" + default variant renders as a solid gray fill.
export const variantForColor = (color: Color): NonNullable<ButtonVariants['variant']> =>
  color === Color.WHITE ? 'outline' : 'default';

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const Button = ({
  ariaLabel,
  children,
  className,
  color = Color.BLUE,
  disabled = false,
  href,
  loading = false,
  loadingWithSpinner = true,
  onClick,
  rel = 'noreferrer nofollow',
  size = 'base',
  target,
  title,
  type = 'button',
  dataTestId
}: Props) => {
  const semanticColor = colorToSemantic[color as Color] ?? 'secondary';
  const semanticSize = sizeToSemantic[size];
  const variant = variantForColor(color as Color);
  const isDisabled = disabled || loading;
  const showSpinner = loading && loadingWithSpinner;
  // Matches the old behavior of replacing children entirely while loading.
  const displayChildren = loading ? (loadingWithSpinner ? null : 'Loading...') : children;
  // The old Spinner set its own aria-label="Loading...", giving the button that accessible
  // name whenever no explicit ariaLabel was set. Replicate that here since the new spinner icon
  // doesn't carry one.
  const resolvedAriaLabel = ariaLabel ?? (showSpinner ? 'Loading...' : undefined);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (!isDisabled && onClick) {
      onClick(event);
    }
  };

  if (href) {
    return (
      <a
        aria-label={resolvedAriaLabel}
        className={buttonVariants({ variant, color: semanticColor, size: semanticSize, className })}
        href={isDisabled ? '#' : href}
        data-testid={dataTestId}
        onClick={handleClick}
        rel={rel}
        target={target}
        title={title}>
        {showSpinner && <FontAwesomeIcon icon={faSpinner} spin className="shrink-0" />}
        {displayChildren}
      </a>
    );
  }

  return (
    <NewButton
      aria-label={resolvedAriaLabel}
      className={className}
      color={semanticColor}
      disabled={isDisabled}
      data-testid={dataTestId}
      isLoading={showSpinner}
      onClick={handleClick}
      size={semanticSize}
      title={title}
      type={type}
      variant={variant}>
      {displayChildren}
    </NewButton>
  );
};
export default React.memo(Button);
